import type {
	CreateResourceDto,
	CreateResourceThemeDto,
	Resource,
	ResourceTheme,
	UpdateResourceDto,
} from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { privateAxios } from "../axios";
import { resourcesQueryKeys } from "../query-keys/resources.query-keys";

const createResource = async (payload: CreateResourceDto): Promise<Resource> => {
	const { data } = await privateAxios.post<Resource>("/resources", payload);
	return data;
};

const updateResource = async ({
	resourceId,
	payload,
}: {
	resourceId: number;
	payload: UpdateResourceDto;
}): Promise<Resource> => {
	const { data } = await privateAxios.patch<Resource>(
		`/resources/${resourceId}`,
		payload,
	);
	return data;
};

const deleteResource = async (resourceId: number): Promise<void> => {
	await privateAxios.delete(`/resources/${resourceId}`);
};

const createResourceTheme = async (
	payload: CreateResourceThemeDto,
): Promise<ResourceTheme> => {
	const { data } = await privateAxios.post<ResourceTheme>(
		"/resources/themes",
		payload,
	);
	return data;
};

const deleteResourceTheme = async (themeId: number): Promise<void> => {
	await privateAxios.delete(`/resources/themes/${themeId}`);
};

export const useCreateResourceMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createResource,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: resourcesQueryKeys.lists(),
			});
			toast.success("Ressource ajoutée avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'ajout de la ressource", {
				description: errorMessage,
			});
		},
	});
};

export const useUpdateResourceMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateResource,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: resourcesQueryKeys.lists(),
			});
			toast.success("Ressource modifiée avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la modification de la ressource", {
				description: errorMessage,
			});
		},
	});
};

export const useDeleteResourceMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteResource,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: resourcesQueryKeys.lists(),
			});
			toast.success("Ressource supprimée avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la suppression de la ressource", {
				description: errorMessage,
			});
		},
	});
};

export const useCreateResourceThemeMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createResourceTheme,
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: resourcesQueryKeys.themes({ type: variables.type }),
			});
			toast.success("Thème ajouté avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'ajout du thème", {
				description: errorMessage,
			});
		},
	});
};

export const useDeleteResourceThemeMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteResourceTheme,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["resources", "themes"],
			});
			toast.success("Thème supprimé avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la suppression du thème", {
				description: errorMessage,
			});
		},
	});
};
